import {
  CircularProgress,
  IconButton,
  PropTypes,
  Tooltip,
} from '@material-ui/core';
import React, { useCallback, useState } from 'react';
import DescriptionIcon from '@material-ui/icons/Description';
import ReceiptIcon from '@material-ui/icons/Receipt';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import { uuid } from 'uuidv4';
import GetAppIcon from '@material-ui/icons/GetApp';
import { useFeedback } from '../../hooks/feedback';
import XlsIcon from '../../assets/icons/xls-file.svg';
import { base64ToUint8Array, getMimeByExt } from '../../utils/file.utils';

interface FileDownloaderProps {
  onSelect?: (value?: any) => any;
  onSelectParams?: unknown;
  icon?: 'pdf' | 'voucher' | 'xls' | 'download' | undefined;
  typeExpected?: string;
  helpText?: string;
  disabled?: boolean;
  bytes?: any;
  defaultFileName?: string;
  color?: PropTypes.Color;
}

const FileDownloader: React.FC<FileDownloaderProps> = ({
  onSelect,
  icon,
  onSelectParams,
  helpText,
  typeExpected = 'pdf',
  disabled,
  bytes,
  defaultFileName,
  color,
}: FileDownloaderProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const { addFeedback } = useFeedback();

  const performDownload = useCallback(
    (data: any, fileName: string | undefined) => {
      try {
        const mimeType = getMimeByExt(typeExpected);
        const blob = new Blob([data], { type: mimeType });
        const a = document.createElement('a');
        const blobURL = URL.createObjectURL(blob);
        a.download = fileName ?? uuid();
        a.href = blobURL;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (error) {
        addFeedback({
          type: 'warning',
          description: 'Não foi possível fazer o download do arquivo!',
        });
      }
    },
    [addFeedback, typeExpected],
  );

  const onClickDownload = useCallback(async () => {
    setLoading(true);
    if (onSelect) {
      const response = await onSelect(onSelectParams);
      if (response && response.statusText !== 'No Content') {
        const headerLine = response.headers['content-disposition'];
        let filename = '';
        if (headerLine) {
          const startFileNameIndex = headerLine.indexOf('"') + 1;
          const endFileNameIndex = headerLine.lastIndexOf('"');
          filename = headerLine.substring(startFileNameIndex, endFileNameIndex);
        }
        performDownload(response?.data, filename ?? uuid());
      } else {
        addFeedback({
          type: 'warning',
          description: 'O arquivo não foi encontrado!',
        });
      }
    } else if (bytes) {
      const convertedByteArray = base64ToUint8Array(bytes);
      performDownload(convertedByteArray, defaultFileName);
    }
    setLoading(false);
  }, [
    onSelect,
    bytes,
    onSelectParams,
    performDownload,
    addFeedback,
    defaultFileName,
  ]);

  const chooseDefaultIcon = useCallback((): JSX.Element => {
    let defaultIcon = <DescriptionIcon />;
    switch (icon) {
      case 'pdf':
        defaultIcon = <PictureAsPdfIcon />;
        break;
      case 'voucher':
        defaultIcon = <ReceiptIcon />;
        break;
      case 'xls':
        defaultIcon = <img src={XlsIcon} alt="Download Excel" />;
        break;
      case 'download':
        defaultIcon = <GetAppIcon />;
        break;
      default:
        break;
    }
    return defaultIcon;
  }, [icon]);

  return (
    <>
      {loading ? (
        <CircularProgress
          size={20}
          style={{ marginLeft: 15, top: 4, position: 'relative' }}
        />
      ) : (
        <Tooltip
          title={helpText ?? 'Clique para iniciar o download'}
          placement="top"
          arrow
        >
          <IconButton
            onClick={onClickDownload}
            disabled={disabled}
            color={color}
          >
            {chooseDefaultIcon()}
          </IconButton>
        </Tooltip>
      )}
    </>
  );
};

export default FileDownloader;
